import { PASSENGER_TYPE_ADULT, PASSENGER_TYPE_CHILD_AGE_3 } from '@/constants';
import * as Sentry from '@sentry/browser';

const initialState = () => {
    return {
        shortTimeHours: window.APP.orderShortTimeHours,

        search: {
            start_place: null,
            start_vendor: null,
            end_place: null,
            end_vendor: null,
            departure_date: null,
            departure_time: null,
            passengers: [],
        },

        vendorAddresses: {
            start: null,
            end: null,
        },

        travel: null,

        passengers: [],

        flightNumber: null,
        flightTime: '',

        travelOptionPrices: null,
        travelOptionComments: null,

        purchaser: {
            purchaser_email: null,
            purchaser_first_name: null,
            purchaser_last_name: null,
            purchaser_phone: null,
        },

        isInvoice: false,
        invoice: {
            email: null,
            company: null,
            vat_number: null,
            address: null,
            zip: null,
            city: null,
            country: null,
        },

        discount: null,
        isCheckedCreateAccount: false,
        password: null,
        passwordConfirmation: null,
        payment_provider: 'p24',
    };
};

const state = initialState;

const getters = {
    search: (state) => {
        return state.search;
    },

    isSearchSet: (state) => {
        return state.search.start_place !== null && state.search.end_place !== null;
    },

    vendorAddresses: (state) => {
        return state.vendorAddresses;
    },

    isVendorStartAddressComplete: (state) => {
        if (!state.vendorAddresses.start) {
            return false;
        }

        return state.vendorAddresses.start.street !== null && state.vendorAddresses.start.street.trim() !== ''
            && state.vendorAddresses.start.building_number !== null && state.vendorAddresses.start.building_number.trim() !== ''
            && state.vendorAddresses.start.zip !== null && state.vendorAddresses.start.zip.trim() !== ''
            && state.vendorAddresses.start.city !== null && state.vendorAddresses.start.city.trim() !== '';
    },

    isVendorEndAddressComplete: (state) => {
        if (!state.vendorAddresses.end) {
            return false;
        }

        return state.vendorAddresses.end.street !== null && state.vendorAddresses.end.street.trim() !== ''
            && state.vendorAddresses.end.building_number !== null && state.vendorAddresses.end.building_number.trim() !== ''
            && state.vendorAddresses.end.zip !== null && state.vendorAddresses.end.zip.trim() !== ''
            && state.vendorAddresses.end.city !== null && state.vendorAddresses.end.city.trim() !== '';
    },

    getTravel: (state) => {
        return state.travel;
    },

    getPassengers: (state) => {
        return state.passengers;
    },

    getFlightNumber: (state) => {
        return state.flightNumber;
    },

    getFlightTime: (state) => {
        return state.flightTime;
    },

    getTravelOptionPrices: (state) => {
        return state.travelOptionPrices;
    },

    getTravelOptionComments: (state) => {
        return state.travelOptionComments;
    },

    getIsInvoice: (state) => {
        return state.isInvoice;
    },

    getInvoice: (state) => {
        return state.invoice;
    },

    getPurchaser: (state) => {
        return state.purchaser;
    },

    getDiscount: (state) => {
        return state.discount;
    },

    getTravelPriceByPassengerType: (state) => (type) => {
        let price = state.travel.gross_price;

        if (type === PASSENGER_TYPE_CHILD_AGE_3) {
            price = state.travel.child_age_3_gross_price;
        }

        return price;
    },

    getTravelTotalByPassengerType: (state, getters) => (type) => {
        return getters.getTravelPriceByPassengerType(type) * getters.passengersTotalByType(type);
    },

    getTravelTotal: (state, getters) => {
        return state.travel.gross_price;
        const adultPrice = getters.getTravelTotalByPassengerType(PASSENGER_TYPE_ADULT);
        const childAge3Price = getters.getTravelTotalByPassengerType(PASSENGER_TYPE_CHILD_AGE_3);

        return adultPrice + childAge3Price;
    },

    getCartBaseTotal: (state, getters) => {
        let optionsTotal = 0;

        if (state.travelOptionPrices) {
            optionsTotal = state.travelOptionPrices
                .map((option) => option.gross_price * option.quantity)
                .reduce((a, b) => a + b, 0);
        }

        return getters.getTravelTotal + optionsTotal;
    },

    getCartDiscountGrossValue: (state, getters, rootState, rootGetters) => {
        if (!state.discount) {
            return 0;
        }

        if (state.discount.discount_type === rootGetters['constants/discountTypePercent']) {
            return getters.getCartBaseTotal * (state.discount.discount_percent / 100);
        }

        if (state.discount.discount_gross_value > getters.getCartBaseTotal) {
            return getters.getCartBaseTotal;
        }

        return state.discount.discount_gross_value;
    },

    getUserDiscountGrossValue: (state, getters, rootState) => {
        if (!rootState.user.discount_percent) {
            return 0;
        }

        return (rootState.user.discount_percent / 100) * (getters.getCartBaseTotal - getters.getCartDiscountGrossValue);
    },

    getCartTotal: (state, getters) => {
        return getters.getCartBaseTotal - getters.getCartDiscountGrossValue - getters.getUserDiscountGrossValue;
    },


    getCartData: (state) => {
        return {
            offer_type: 'berbus',

            travel_id: state.travel.id,
            promo_price_id: state.travel.promo_price_id ? state.travel.promo_price_id : null,
            discount_code_id: state.discount ? state.discount.id : null,

            departure_date: `${state.search.departure_date} ${state.search.departure_time}`,
            has_return_travel: false,

            passengers: state.passengers,
            passenger_quantities: state.search.passengers,

            flight_time: state.flightTime,

            travel_options: state.travelOptionPrices,
            travel_options_comments: state.travelOptionComments,

            ...state.purchaser,

            is_invoice: state.isInvoice,
            invoice: state.isInvoice ? state.invoice : null,

            create_account: state.isCheckedCreateAccount,
            password: state.password,
            password_confirmation: state.passwordConfirmation,
            payment_provider: state.payment_provider,
        };
    },

    isCheckedCreateAccount: (state) => {
        return state.isCheckedCreateAccount;
    },

    additionalOptionsTotal: (state, getters) => {
        if (!getters.travelOptionPrices) {
            return 0;
        }

        return getters.travelOptionPrices.reduce(function(previous, current){
            return previous + (current.gross_price * current.quantity);
        }, 0);
    },

    passengersTotalByType: (state) => (type = null) => {
        let passengers = state.search.passengers;

        if (type) {
            passengers = passengers.filter((passenger) => passenger.type === type);
        }

        if (passengers.length === 0) {
            return 0;
        }

        return passengers
            .map((passenger) => passenger.quantity)
            .reduce((accumulator, quantity) => accumulator + quantity);
    },

    passengersTotal: (state, getters) => {
        return getters.passengersTotalByType();
    },
};

const mutations = {
    setSearch(state, payload) {
        Sentry.addBreadcrumb({message: 'setSearchM', data: payload});
        state.search = payload;
    },

    setVendorStartAddress(state, payload) {
        Sentry.addBreadcrumb({message: 'setVendorStartAddressM', data: payload});
        state.vendorAddresses.start = payload;
    },

    setVendorEndAddress(state, payload) {
        Sentry.addBreadcrumb({message: 'setVendorEndAddressM', data: payload});
        state.vendorAddresses.end = payload;
    },

    setTravel(state, travel) {
        if (travel.promo_price) {
            travel.promo_price_id = travel.promo_price.promo_price_id;

            travel.net_price = travel.promo_price.net_price;
            travel.gross_price = travel.promo_price.gross_price;
            travel.vat = travel.promo_price.vat;

            travel.child_age_3_net_price = travel.promo_price.child_age_3_net_price;
            travel.child_age_3_gross_price = travel.promo_price.child_age_3_gross_price;
            travel.child_age_3_vat = travel.promo_price.child_age_3_vat;

            delete travel.promo_price;
        }

        state.travel = travel;
    },

    setPassengers(state, passengers) {
        state.passengers = passengers;
    },

    setFlightNumber(state, number) {
        state.flightNumber = number;
    },

    setFlightTime(state, time) {
        state.flightTime = time;
    },

    setTravelOptionPrices(state, options) {
        state.travelOptionPrices = options;
    },

    setTravelOptionComments(state, comments) {
        state.travelOptionComments = comments;
    },

    setIsInvoice(state, bool) {
        state.isInvoice = bool;
    },

    setInvoice(state, invoice) {
        state.invoice = invoice;
    },

    setPurchaser(state, parameters) {
        state.purchaser = parameters;
    },

    setDiscount(state, discount) {
        state.discount = discount;
    },

    setIsCheckedCreateAccount(state, value) {
        state.isCheckedCreateAccount = value;
    },

    setPassword(state, password) {
        state.password = password;
    },

    setPasswordConfirmation(state, password) {
        state.passwordConfirmation = password;
    },

    setPaymentMethod(state, paymentMethod) {
        state.payment_provider = paymentMethod;
    },

    reset(state) {
        const initial = initialState();

        Object.keys(initial).forEach((key) => {
            state[key] = initial[key];
        });
    },
};

const actions = {
    setSearch({ commit }, payload) {
        Sentry.addBreadcrumb({message: 'setSearch', data: payload});
        commit('setSearch', payload);
    },

    setVendorStartAddress({ commit }, payload) {
        Sentry.addBreadcrumb({message: 'setVendorStartAddress', data: payload});
        commit('setVendorStartAddress', payload);
    },

    setVendorEndAddress({ commit }, payload) {
        Sentry.addBreadcrumb({message: 'setVendorEndAddress', data: payload});
        commit('setVendorEndAddress', payload);
    },

    setTravel({ commit }, travel) {
        commit('setTravel', travel);
    },

    setPassengers({ commit }, passengers) {
        commit('setPassengers', passengers);
    },

    setFlightNumber({ commit }, number) {
        commit('setFlightNumber', number);
    },

    setFlightTime({ commit }, time) {
        commit('setFlightTime', time);
    },

    setTravelOptionPrices({ commit }, options) {
        commit('setTravelOptionPrices', options);
    },

    setTravelOptionComments({commit}, comments) {
        commit('setTravelOptionComments', comments);
    },

    setIsInvoice({ commit }, bool) {
        commit('setIsInvoice', bool);
    },

    setInvoice({ commit }, invoice) {
        commit('setInvoice', invoice);
    },

    setPurchaser({ commit }, parameters) {
        commit('setPurchaser', parameters);
    },

    setDiscount({ commit }, discount) {
        commit('setDiscount', discount);
    },

    setIsCheckedCreateAccount({ commit }, value) {
        commit('setIsCheckedCreateAccount', value);
    },

    setPassword({ commit }, password) {
        commit('setPassword', password);
    },

    setPasswordConfirmation({ commit }, password) {
        commit('setPasswordConfirmation', password);
    },

    setPaymentMethod({ commit}, paymentMethod) {
        commit('setPaymentMethod', paymentMethod);
    },

    reset({ commit }) {
        commit('reset');
    },
};


export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
